import React, { useCallback, useState, useEffect } from 'react';
import {
  Link,
  withRouter,
  matchPath,
  useLocation,
} from 'react-router-dom';
import Icon from '../../components/AppIcon';

// pop-ups
import { toast } from 'react-toastify';

import styled from 'styled-components';

// i18n
import { withTranslation } from 'react-i18next';

// drag and drop
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';

// redux
import { push } from '../../navigation';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  deletePart,
  duplicatePart,
  movePart,
  updatePart,
  addChapters,
  toggleChapterUpload,
  updateContentVersion,
} from '../../modules/book';
import { updateSpineIndex } from '../../modules/readerApp';

import { createBookPost } from '../../modules/bookPost';

import ChapterUploadModal from '../content/chapter/upload/chapterUploadModal';

import ContentUpdateConfirmationModal from '../content/contentUpdateConfirmationModal';

import accessUtil from '../../utils/accessUtil';
import authUtil from '../../utils/authUtil';

const PartListContainer = styled.div`
  height: 100%;
  position: relative;
  color: var(--br-text);
`;

const PartList = styled.div`
  display: grid;
  gap: 2px;
`;

const PartRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 7px;
  background: ${({$active}) => $active ? 'var(--br-raised-surface)' : 'transparent'};
`;

const PartLink = styled.button`
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: inherit;
  text-align: left;
  font: inherit;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid var(--br-focus);
  }
`;

const Menu = styled.details`
  position: relative;
  flex-shrink: 0;
  summary {
    cursor: pointer;
    list-style: none;
    padding: 7px;
    border-radius: 7px;
  }
  summary::-webkit-details-marker { display: none; }
  summary:focus-visible { outline: 2px solid var(--br-focus); }
`;

const MenuOptions = styled.div`
  position: absolute;
  right: 0;
  z-index: 10;
  min-width: 180px;
  padding: 5px;
  border: 1px solid var(--br-border);
  border-radius: 8px;
  background: var(--br-surface);
  box-shadow: 0 8px 24px rgba(0, 0, 0, .18);
  button, a {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    box-sizing: border-box;
    padding: 8px;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: var(--br-text);
    text-align: left;
    text-decoration: none;
    font: inherit;
    cursor: pointer;
  }
  button:hover, a:hover { background: var(--br-raised-surface); }
`;

const MovingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  background: rgba(0, 0, 0, .25);
`;

export const AddPartMenu = ({
  currentBook,
  contentVersion,
  partIcons,
  t,
  changePage,
  toggleChapterUpload,
}) => (
  <Menu>
    <summary>{t('Add')}</summary>
    <MenuOptions>
      <button type="button" onClick={() => toggleChapterUpload(true)}>
        <Icon name="upload" /> {t('UploadChapters')}
      </button>
      <button
        type="button"
        data-book-id={currentBook._id}
        onClick={() => {
            changePage(
              `/books/${currentBook._id}/content/v${contentVersion.versionNumber}/new-chapter`
            );
        }}
      >
        <Icon name={partIcons['Chapter']} /> {t('NewChapter')}
      </button>
      <Link
        to={`/books/${currentBook._id}/content/v${contentVersion.versionNumber}/new-survey`}
        data-book-id={currentBook._id}
      >
        <Icon name={partIcons['Survey']} /> {t('NewSurvey')}
      </Link>
    </MenuOptions>
  </Menu>
);

export const PartsList = ({
  currentContentVersion,
  currentBook,
  currentSpineIndex,
  partIcons,
  showChapterUpload,
  inverted,
  size,

  changePage,
  createBookPost,
  deletePart,
  duplicatePart,
  updatePart,
  updateSpineIndex,
  t,
  toggleChapterUpload,
  updateContentVersion,
}) => {
  const [localContentVersion, setLocalContentVersion] = useState();
  const [movingPart, setMovingPart] = useState();
  const [confirmMoveOpen, setConfirmMoveOpen] = useState(false);
  const location = useLocation();
  const match = matchPath(
    location.pathname,
    location.pathname === '/read'
      ? {
          path: '/read',
        }
      : {
          path: '/books/:bookId/content/v:versionNumber/:partId',
        }
  );
  // set localcontentversion whenever the prop changes
  useEffect(() => {
    setLocalContentVersion(currentContentVersion);
  }, [currentContentVersion]);

  useEffect(() => {
    if (!movingPart) {
      return;
    }
    const { affectedReaders } = movingPart;
    if (!affectedReaders || affectedReaders.length === 0) {
      // no readers affected, move without awaiting confirmation
      _movePart();
    } else {
      setConfirmMoveOpen(true);
    }
  }, [movingPart, confirmMoveOpen]);

  const editable = useCallback(
    () => accessUtil.isAllowed(currentBook?.access, ['edit']),
    [currentBook?.access]
  );

  if (!currentBook) {
    return null;
  }

  const handleDeletePartClick = async (event, data) => {
    event.preventDefault();
    event.stopPropagation();
    const { partId } = event.currentTarget.dataset;
    const idToken = await authUtil.getFreshIdToken();
    deletePart(idToken, currentBook, partId, localContentVersion);
  };
  const handleDuplicatePartClick = async event => {
    event.preventDefault();
    event.stopPropagation();
    const { partId } = event.currentTarget.dataset;
    const idToken = await authUtil.getFreshIdToken();
    duplicatePart(idToken, currentBook, partId, localContentVersion);
  };
  const handlePartStartNotificationClick = async event => {
    event.preventDefault();
    event.stopPropagation();
    const { partIndex } = event.currentTarget.dataset;
    const idToken = await authUtil.getFreshIdToken();
    const part = {
      ...localContentVersion.parts[parseInt(partIndex)],
    };
    if (part) {
      part.notifyWhenStarted = !part.notifyWhenStarted;
      updatePart(idToken, currentBook, part);
    }
  };
  const handlePartCompleteNotificationClick = async (event, data) => {
    event.preventDefault();
    event.stopPropagation();
    const { partIndex } = event.currentTarget.dataset;
    const idToken = await authUtil.getFreshIdToken();
    const part = {
      ...localContentVersion.parts[parseInt(partIndex)],
    };
    if (part) {
      part.notifyWhenCompleted = !part.notifyWhenCompleted;
      updatePart(idToken, currentBook, part);
    }
  };
  const getPartMenu = (part, index) => (
    <Menu className="part-menu" onClick={event => event.stopPropagation()}>
      <summary aria-label={`${t('Actions')} ${part.title || t('Chapter')}`}>
        <Icon name="ellipsis vertical" />
      </summary>
      <MenuOptions>
        {part.kind === 'Chapter' && (
          <>
            <button
              type="button"
              className="start-notification"
              data-part-index={index}
              onClick={handlePartStartNotificationClick}
            >
              <Icon
                color={part.notifyWhenStarted ? 'green' : undefined}
                name={part.notifyWhenStarted ? 'toggle on' : 'toggle off'}
              />
              {t('NotifyMe')} {t('WhenReadersStart')}
            </button>
            <button
              type="button"
              data-part-index={index}
              onClick={handlePartCompleteNotificationClick}
            >
              <Icon
                color={part.notifyWhenCompleted ? 'green' : undefined}
                name={part.notifyWhenCompleted ? 'toggle on' : 'toggle off'}
              />
              {t('NotifyMe')} {t('WhenReadersComplete')}
            </button>
          </>
        )}
        {part.kind === 'Survey' && editable() && (
          <button
            type="button"
            data-part-id={part._id}
            onClick={handleDuplicatePartClick}
          >
            <Icon name="clone" /> {t('Duplicate')}
          </button>
        )}
        {editable() && (
          <button
            type="button"
            data-part-id={part._id}
            onClick={handleDeletePartClick}
          >
            <Icon name="trash alternate" color="red" /> {t('Delete')}
          </button>
        )}
      </MenuOptions>
    </Menu>
  );
  const getPartContent = part => {
    return (
      <span style={{minWidth: 0}}>
        <strong
          style={{
            maxWidth: 150,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
          }}
        >
          {part.title}
        </strong>
        <small>
          {part.kind === 'Chapter' && (
            <span>{t('WordCountString', { count: part.wordCount })}</span>
          )}
          {part.kind === 'Survey' && part.questions && (
            <span>
              {t('QuestionCountString', { count: part.questions.length })}
            </span>
          )}
        </small>
      </span>
    );
  };

  const getPartsList = () => {
    const scrollMainToTop = () => {
      const main = document.getElementById('main');
      if (main) main.scrollTop = 0;
    };
    if (currentBook?.parts?.length > 0 && location?.pathname === '/read') {
      return currentBook.parts.map((part, index) => (
        <PartRow key={part._id} $active={index === currentSpineIndex}>
          <PartLink
            aria-current={index === currentSpineIndex ? 'page' : undefined}
            onClick={() => {
              scrollMainToTop();
              updateSpineIndex(index);
            }}
          >
            <Icon name={partIcons[part.kind]} size="large" />
            {getPartContent(part)}
          </PartLink>
        </PartRow>
      ));
    }
    const visibleVersion = localContentVersion || currentContentVersion;
    if (!visibleVersion) return null;
    return visibleVersion.parts.map((part, index) => (
      <Draggable draggableId={part._id} index={index} key={part._id}>
        {provided => (
          <PartRow
            ref={provided.innerRef}
            {...provided.draggableProps}
            $active={match?.params?.partId === part._id}
          >
            <PartLink
              {...provided.dragHandleProps}
              aria-current={match?.params?.partId === part._id ? 'page' : undefined}
              onClick={() => {
                scrollMainToTop();
                const newPage = `/books/${currentBook._id}/content/v${visibleVersion.versionNumber}/${part._id}`;
                changePage(newPage);
              }}
            >
              <Icon name={partIcons[part.kind]} size="large" />
              {getPartContent(part)}
            </PartLink>
            {getPartMenu(part, index)}
          </PartRow>
        )}
      </Draggable>
    ));
  };

  const getAffectedReaders = async (partId, from, to) => {
    const idToken = await authUtil.getFreshIdToken();
    return fetch(
      `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/books/${currentBook._id}/content/${localContentVersion._id}/affected-readers`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'br-token': idToken,
        },
        body: JSON.stringify({ partId, from, to }),
      }
    ).then(res => {
      if (res.ok) {
        return res.json();
      }
      throw new Error('Failed to delete chapter');
    });
  };

  const onDragEnd = data => {
    const { source, destination, draggableId } = data;
    if (!source || !destination) {
      // abort
      return;
    }
    prepareToMovePart(draggableId, source.index, destination.index);
  };

  const prepareToMovePart = async (partId, from, to) => {
    const part = localContentVersion.parts[from];
    try {
      const affectedReaders = await getAffectedReaders(partId, from, to);
      setMovingPart({
        title: part.title,
        partId,
        from,
        to,
        affectedReaders,
      });
    } catch (e) {
      toast.error('Failed to move part', e);
    }
  };
  const cancelMovePart = () => {
    setConfirmMoveOpen(false);
    setMovingPart(false);
  };
  const _movePart = async updateText => {
    const idToken = await authUtil.getFreshIdToken();
    // clone the parts list for the optimistic render only — what goes over
    // the wire is the move itself, so a stale list can never delete anything
    const newPartsList = [...localContentVersion.parts];
    const part = newPartsList[movingPart.from];
    newPartsList.splice(movingPart.from, 1);
    newPartsList.splice(movingPart.to, 0, part);
    updateContentVersion(
      idToken,
      {
        bookId: currentBook._id,
        contentVersionId: localContentVersion._id,
        move: {
          partId: movingPart.partId,
          toIndex: movingPart.to,
        },
        expectedUpdatedAt: localContentVersion.updatedAt,
        bookPost: {
          text: updateText,
        },
      },
      (ok, err) => {
        if (!ok) {
          // the server refused the write; updateContentVersion has already
          // refetched the book, so roll the optimistic list back
          setLocalContentVersion(currentContentVersion);
          toast.error(
            err?.isConflict
              ? t('BookChangedElsewhereReload')
              : t('SomethingWentWrong')
          );
          setConfirmMoveOpen(false);
          setMovingPart(false);
          return;
        }
        // create update message
        if (updateText) {
          createBookPost(idToken, currentBook._id, { text: updateText });
        }
        toast.info(
          t('MovedXFromYtoZ', {
            x: movingPart?.title,
            y: movingPart?.from + 1,
            z: movingPart?.to + 1,
          })
        );
        setConfirmMoveOpen(false);
        setMovingPart(false);
      }
    );

    setLocalContentVersion({
      ...localContentVersion,
      parts: newPartsList,
    });
  };

  const partsList = getPartsList();
  const addPart = localContentVersion && editable() && (
    <AddPartMenu
      currentBook={currentBook}
      contentVersion={localContentVersion}
      partIcons={partIcons}
      t={t}
      changePage={changePage}
      toggleChapterUpload={toggleChapterUpload}
    />
  );
  if (partsList) {
    return (
      <PartListContainer>
        {partsList.length > 10 &&
          accessUtil.isAllowed(currentBook.access, ['edit']) && (
            <div>{addPart}</div>
          )}
        <DragDropContext key="drag-drop-context" onDragEnd={onDragEnd}>
          <Droppable droppableId="partlist">
            {provided => (
              <PartList ref={provided.innerRef} {...provided.droppableProps}>
                {partsList}
                {provided.placeholder}
              </PartList>
            )}
          </Droppable>
        </DragDropContext>
        {accessUtil.isAllowed(currentBook.access, ['edit']) && (
          <div>{addPart}</div>
        )}
        {showChapterUpload && (
          <ChapterUploadModal
            key="uploadModal"
            open
            onClose={() => toggleChapterUpload(false)}
            toggleOpen={() => toggleChapterUpload(true)}
          />
        )}
        {!!movingPart && confirmMoveOpen && (
          <ContentUpdateConfirmationModal
            key="confirm-move-part"
            open
            affectedReaders={movingPart.affectedReaders}
            updateText={
              movingPart &&
              t('MovedXFromYtoZ', {
                x: movingPart.title,
                y: movingPart.from + 1,
                z: movingPart.to + 1,
              })
            }
            onCancel={cancelMovePart}
            onConfirm={_movePart}
            cancelButton={t('No')}
            confirmButton={t('Yes')}
          />
        )}
        {!!movingPart && <MovingOverlay aria-hidden="true" />}
      </PartListContainer>
    );
  }
  return null;
};

// redux stuff
const mapStateToProps = state => ({
  user: state.user.user,
  idToken: state.user.idToken,
  currentBook: state.book.currentBook,
  showChapterUpload: state.book.showChapterUpload,
  pendingChapters: state.book.pendingChapters,
  currentContentVersion: state.book.currentContentVersion,
  chapterUploadMessage: state.book.chapterUploadMessage,
  partIcons: state.app.partIcons,
  currentSpineIndex: state.readerApp.currentSpineIndex,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      deletePart,
      duplicatePart,
      movePart,
      updatePart,
      addChapters,
      toggleChapterUpload,
      updateContentVersion,
      createBookPost,
      updateSpineIndex,
      changePage: newPage => push(newPage),
    },
    dispatch
  );

export default withTranslation()(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(PartsList))
);
